/**
 * Access control for AimerPay - Adyen Balance Platform
 *
 * Role Hierarchy:
 * - SuperAdmin: Platform management + can enter any MerchantOwner context
 * - MerchantOwner: Full access to all features
 * - MerchantAccountant: View transactions, reports, process refunds
 * - MerchantViewer: View transactions only
 */

import { getActingAsMerchantOwner } from '@/models/actingAsMerchant';

// Role constants
export const ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  MERCHANT_OWNER: 'MerchantOwner',
  MERCHANT_ACCOUNTANT: 'MerchantAccountant',
  MERCHANT_VIEWER: 'MerchantViewer',
} as const;

// Helper to check if user has merchants using user-specific localStorage keys
const checkHasMerchants = (userId?: string) => {
  if (!userId) return false;
  const merchantsKey = `aimerpay_merchants_${userId}`;
  const merchantsJson = localStorage.getItem(merchantsKey);
  if (merchantsJson) {
    try {
      const merchants = JSON.parse(merchantsJson);
      return Array.isArray(merchants) && merchants.length > 0;
    } catch {
      return false;
    }
  }
  return false;
};

export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const role = currentUser?.role || currentUser?.access;
  const userId = currentUser?.id || currentUser?.userid;

  // Check specific roles
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const isOwner = role === ROLES.MERCHANT_OWNER;
  const isAccountant = role === ROLES.MERCHANT_ACCOUNTANT;
  const isViewer = role === ROLES.MERCHANT_VIEWER;

  // Check if user has merchants (using user-specific key)
  const hasMerchants = checkHasMerchants(userId);

  // SuperAdmin merchant owner mode: read from sessionStorage
  const actingAsMerchantOwner = isSuperAdmin
    ? getActingAsMerchantOwner()
    : null;
  const inMerchantMode = isSuperAdmin && !!actingAsMerchantOwner;

  return {
    // Basic access - any logged in user
    canAccess: !!currentUser,

    // SuperAdmin access
    isSuperAdmin,
    canViewSplitConfigurations: isSuperAdmin,
    canInviteAdmin: isSuperAdmin,

    // Admin panel - SuperAdmin only, hidden in merchant mode
    canViewAdmin: isSuperAdmin && !inMerchantMode,

    // Whether user has merchants
    hasMerchants: inMerchantMode || hasMerchants,

    // Role checks
    isOwner,
    isAccountant,
    isViewer,

    // Acting as merchant owner (for banner and menu filtering)
    actingAsMerchantOwner,
    inMerchantMode,

    // SuperAdmin can view all partners (merchant list)
    canViewAllPartners: isSuperAdmin,

    // Dashboard - SuperAdmin in merchant mode also sees dashboard
    canViewDashboard: (!!currentUser && !isSuperAdmin) || inMerchantMode,

    // Partners/Merchants - Owner can always access; SuperAdmin only in merchant mode
    canViewPartners: isOwner || inMerchantMode,
    canManagePartners: isOwner || inMerchantMode,
    canCreatePartner: isOwner || inMerchantMode,

    // Legacy aliases
    canViewAccountHolders: isOwner || inMerchantMode,
    canManageAccountHolders: isOwner || inMerchantMode,
    canCreateAccountHolder: isOwner || inMerchantMode,

    // Balance Accounts - Owner only WITH merchants, or SuperAdmin in merchant mode
    canViewBalanceAccounts: (isOwner && hasMerchants) || inMerchantMode,
    canManageBalanceAccounts: (isOwner && hasMerchants) || inMerchantMode,
    canCreateBalanceAccount: (isOwner && hasMerchants) || inMerchantMode,

    // Transactions - SuperAdmin in merchant mode also gets access
    canViewTransactions:
      (!!currentUser && hasMerchants && !isSuperAdmin) || inMerchantMode,

    // Payouts - SuperAdmin in merchant mode also gets access
    canViewPayouts:
      ((isOwner || isAccountant) && hasMerchants) || inMerchantMode,

    // Reports - SuperAdmin in merchant mode also gets access
    canViewReports:
      ((isOwner || isAccountant) && hasMerchants) || inMerchantMode,

    // Disputes - SuperAdmin in merchant mode also gets access
    canViewDisputes:
      ((isOwner || isAccountant) && hasMerchants) || inMerchantMode,

    // Staff Management - Owner only (NOT for SuperAdmin in merchant mode)
    canInviteStaff: isOwner,
    canManageStaff: isOwner,

    // Settings - all users except SuperAdmin in merchant mode
    canViewSettings: !!currentUser && !inMerchantMode,
  };
}
